import type React from "react";
import { type MouseEvent, type ReactNode, useReducer } from "react";
import type { IconButtonProps } from "@mui/material";
import ToggleExpandButton from "./ToggleExpandButton";

type ExpandControlProps = {
  /** Uncontrolled default. Seeds initial state when `expanded` is omitted. */
  defaultExpanded?: boolean;
  /**
   * Controlled mode. When provided, `expanded` is the source of truth and `onExpandedChange`
   * is called on every toggle attempt. When omitted, the component manages state internally
   * (uncontrolled, original behavior).
   */
  expanded?: boolean;
  /** Required when using controlled mode (`expanded` is provided). */
  onChange?: (next: boolean) => void;
  children: (props: {
    isExpanded: boolean;
    toggleExpand: () => void;
    ToggleButton: React.JSX.Element;
  }) => ReactNode;
  iconButtonProps?: Omit<IconButtonProps, "onClick" | "disabled"> & {
    disabled?: boolean;
    onClick?: (e: MouseEvent<HTMLButtonElement>, isExpanded: boolean) => void;
  };
};

const ExpandControl = ({
  defaultExpanded = true,
  expanded,
  onChange,
  iconButtonProps,
  children,
}: ExpandControlProps) => {
  const {
    disabled,
    onClick: iconButtonOnClick,
    ...restIconButtonProps
  } = iconButtonProps ?? {};

  const [internalExpanded, toggleInternal] = useReducer((s) => !s, defaultExpanded);
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;

  const toggleExpand = () => {
    if (isControlled) {
      onChange?.(!isExpanded);
    } else {
      toggleInternal();
    }
  };

  const onToggleExpandButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    toggleExpand();
    iconButtonOnClick?.(e, isExpanded);
  };

  return children({
    isExpanded,
    toggleExpand,
    ToggleButton: (
      <ToggleExpandButton
        isToggled={isExpanded}
        {...restIconButtonProps}
        onClick={onToggleExpandButtonClick}
      />
    ),
  });
};

export default ExpandControl;
